/**
 * The Watching rail's rows (NEWS-297; kerf UI, NM-YGX7HY).
 *
 * Each topic is a kerf UI `ListActionRow`: the primary button selects the
 * topic (and carries the row's gestures — click, double-click solo, the
 * keyboard), and its trailing "…" is a real `PopupMenu` holding the topic
 * actions. The same menu opens at the pointer on a right-click
 * (`openPopupMenuAt`, wired in `app.tsx`), which is how a row's overflow menu
 * doubles as its context menu — so touch, keyboard and mouse share one menu
 * and there is no separate mobile action bar any more (NM-240's bar and the
 * hand-built `.menu` popup both went with this).
 *
 * Rendering only. The menu items carry `data-menu-action` + `data-topic-id`
 * and are handled by `delegate()` in `app.tsx`, like every other gesture.
 * Domain metadata rides the row's `rootAttributes` (`data-topic-row`,
 * `data-key`, state markers for tests); the component owns its classes,
 * selection semantics (`pressed` → `aria-pressed`) and styling.
 */

import { Badge } from '@kerfjs/ui/badge';
import { ListActionRow } from '@kerfjs/ui/list-action-row';
import { ListItem } from '@kerfjs/ui/list-item';
import type { PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import { Row } from '@kerfjs/ui/row';
import type { SafeHtml } from 'kerfjs';

import { BUILTIN_CATEGORIES, categoryLabel } from '../../../categories.js';
import { isAwaitingSense, type Topic } from '../../../db/schemas.js';
import { selectedFocusLabels } from '../../../topic-focus.js';
import { icon } from '../../components/icons.js';
import { relativeTime } from '../../lib/dates.js';
import { barGraphJsx } from '../pulse/pulse-view.js';

/** What a row's action menu says, computed from the selection it would act on. */
export interface TopicMenuSpec {
  /** " 3 topics" when the row's menu acts on a multi-row selection, else "". */
  suffix: string;
  /** Whether any target is enabled (the menu offers Disable), else Enable. */
  anyActive: boolean;
  /** Whether every target is soloed (the menu offers Unsolo). */
  allSoloed: boolean;
  /** Flagged off-topic stories across the targets (Review Flagged, NM-61). */
  flaggedCount: number;
  /** Whether the menu acts on exactly one topic (Edit is single-topic only). */
  single: boolean;
}

/**
 * The topic actions (NEWS-90, NM-201, NM-61). Enable/disable is the plan-
 * capacity control; a mixed selection resolves toward disabling (the action
 * that changes the most rows), matching what runs.
 */
function topicMenuItems(topicId: string, spec: TopicMenuSpec): PopupMenuEntry[] {
  const attrs = (action: string): { 'data-menu-action': string; 'data-topic-id': string } => ({
    'data-menu-action': action,
    'data-topic-id': topicId,
  });
  return [
    {
      label: `${spec.anyActive ? 'Disable' : 'Enable'}${spec.suffix}`,
      action: 'topic-menu',
      icon: icon(spec.anyActive ? 'pause' : 'play'),
      attributes: attrs('pause'),
    },
    {
      // "Edit topic", not "Rename" (NEWS-162): the name is the question put to the
      // model, and since NM-27 the dialog also carries the topic's focus angles.
      label: 'Edit Topic…',
      action: 'topic-menu',
      icon: icon('pencil'),
      disabled: !spec.single,
      disabledReason: spec.single ? undefined : 'Edit one topic at a time',
      attributes: attrs('rename'),
    },
    { kind: 'divider' },
    {
      label: `${spec.allSoloed ? 'Unsolo' : 'Solo'}${spec.suffix}`,
      action: 'topic-menu',
      icon: icon('solo'),
      attributes: attrs('solo'),
    },
    {
      label: 'Review Flagged Stories',
      action: 'topic-menu',
      icon: icon('flag'),
      disabled: spec.flaggedCount === 0,
      disabledReason: spec.flaggedCount === 0 ? 'No flagged stories' : undefined,
      details: spec.flaggedCount > 0 ? <Badge size="compact">{String(spec.flaggedCount)}</Badge> : undefined,
      attributes: attrs('review-flagged'),
    },
    { kind: 'divider' },
    {
      label: `Delete${spec.suffix}`,
      action: 'topic-menu',
      icon: icon('delete'),
      tone: 'danger',
      attributes: attrs('delete'),
    },
  ];
}

/** The row's status line — the live fact first (NM-92, NM-163), then the stamp. */
function topicStatus(topic: Topic, checking: boolean, hasStories: boolean): string {
  if (checking) return 'Checking sources…';
  if (isAwaitingSense(topic)) return 'Needs attention — pick a sense';
  if (topic.paused) return 'Paused';
  if (topic.lastCheckedAt === null) return 'Not checked yet';
  // "checked 1d ago" alone implied we were holding what it found, so an empty
  // feed qualifies it (NEWS-273).
  return `Checked ${relativeTime(topic.lastCheckedAt)}${hasStories ? '' : ' · no stories'}`;
}

/**
 * A topic row (kerf UI `ListActionRow`).
 *
 * The label is the name, with the rare status flags (solo, needs-attention)
 * before it and today's story count (NEWS-242, hidden at zero) after it. The
 * description carries the tags — the disambiguating sense (NM-164), the
 * section, catalog/custom (NM-224) — and the focus steer (NM-27, wrapping in
 * full only for a sole selection, NEWS-143). Last comes the activity line: the
 * 7-day bars (NM-229) left, the status ("Checked …") right (NM-QZGJVJ).
 */
export function topicRowJsx(
  topic: Topic,
  selected: boolean,
  soloed: boolean,
  dimmed: boolean,
  /** This row is the *only* one selected — see the guidance clamp (NEWS-143). */
  soleSelection: boolean,
  /** Stories found today for this topic; 0 renders nothing (NEWS-242). */
  todayCount: number,
  /** Whether this topic holds any stories at all (NEWS-273). */
  hasStories: boolean,
  /** Seven local-day story counts for the rail activity sparkline (NEWS-453). */
  pulse: readonly number[] | undefined,
  /** Whether a check is in flight for this topic right now (NM-92). */
  checking: boolean,
  /** What this row's action menu acts on and says. */
  menu: TopicMenuSpec,
): SafeHtml {
  const awaiting = isAwaitingSense(topic);
  const focusText = selectedFocusLabels(topic.focus).join(', ');
  const label = (
    <span class="topic-label">
      {soloed ? icon('solo', 13) : ''}
      {awaiting ? icon('warn', 13) : ''}
      <span data-topic-name>{topic.name}</span>
      {todayCount > 0 ? (
        <Badge
          size="compact"
          tone="info"
          label={`${String(todayCount)} ${todayCount === 1 ? 'story' : 'stories'} found today`}
          rootAttributes={{ 'data-today-count': String(todayCount) }}
        >
          {String(todayCount)}
        </Badge>
      ) : (
        ''
      )}
    </span>
  );
  // The activity line (NM-QZGJVJ): the 7-day bars on the left and the status
  // ("Checked 2h ago") on the right, on a line of their own rather than the
  // bars trailing the tags. It rides in the description, not the row's
  // `status` slot: that slot shrink-wraps its content, so nothing in it can
  // span the row. Without bars the status keeps the line alone.
  const activity = (
    <Row gap="xs" vAlign="middle" hAlign="space-between" rootAttributes={{ 'data-topic-activity': '' }}>
      {pulse === undefined
        ? null
        : barGraphJsx(
            pulse,
            `${topic.name}: stories per day, last 7 days (${String(pulse.reduce((sum, count) => sum + count, 0))} total)`,
          )}
      <span data-topic-status="">{topicStatus(topic, checking, hasStories)}</span>
    </Row>
  );
  const description = (
    <span class="topic-description">
      <Row gap="2xs" wrap vAlign="middle">
        {topic.chosenSense === null ? null : (
          <Badge size="compact" tone="pop" rootAttributes={{ 'data-topic-sense': '' }}>
            {topic.chosenSense}
          </Badge>
        )}
        {topic.category === null ? null : (
          <Badge size="compact" appearance="outline" rootAttributes={{ 'data-topic-category': topic.category }}>
            {categoryLabel(BUILTIN_CATEGORIES, topic.category, topic.subcategory)}
          </Badge>
        )}
        <Badge
          size="compact"
          appearance="outline"
          rootAttributes={{ 'data-topic-kind': topic.catalog ? 'catalog' : 'custom' }}
        >
          {topic.catalog ? 'Catalog' : 'Custom'}
        </Badge>
      </Row>
      {focusText === '' ? (
        ''
      ) : (
        <span class="topic-focus" data-expanded={soleSelection ? 'true' : undefined}>
          {focusText}
        </span>
      )}
      {activity}
    </span>
  );
  return (
    <ListActionRow
      action="select-topic"
      label={label}
      description={description}
      busy={checking}
      multiline
      pressed={selected}
      accessibleLabel={topic.name}
      trailingActionVisibility="interaction"
      trailingMenu={{
        label: `Actions for ${topic.name}`,
        icon: icon('more'),
        items: topicMenuItems(topic.id, menu),
      }}
      rootAttributes={{
        'data-key': topic.id,
        'data-topic-row': topic.id,
        'data-topic-selected': selected ? 'true' : undefined,
        'data-topic-soloed': soloed ? 'true' : undefined,
        'data-topic-dimmed': dimmed ? 'true' : undefined,
        'data-topic-paused': topic.paused ? 'true' : undefined,
        'data-topic-checking': checking ? 'true' : undefined,
        'data-topic-attention': awaiting ? 'true' : undefined,
      }}
    />
  );
}

/**
 * A pending-topic placeholder (NM-168): the row shown the moment the user adds
 * a topic, before the create POST returns the real one. A disabled, busy
 * `ListItem` — no `data-topic-row`, so none of the rail's gestures reach it —
 * replaced by the real row the instant it lands.
 */
export function pendingTopicRowJsx(name: string, key: string): SafeHtml {
  return (
    <ListItem
      action="pending-topic"
      label={name}
      status="Adding…"
      busy
      disabled
      rootAttributes={{ 'data-key': key, 'data-pending-topic': name }}
    />
  );
}
